import { AlertTriangle, Inbox, Loader2, ShieldAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The four states every view owes the user: loading, empty, error, forbidden.
 * One implementation, so they look the same on every page and none gets skipped.
 */

export function LoadingRows({ colSpan, label = 'Loading…' }: { colSpan: number; label?: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        {label}
      </td>
    </tr>
  )
}

/**
 * The same waiting state outside a table. `LoadingRows` returns a bare `<tr>`,
 * which is invalid anywhere but inside a `<tbody>` — React logs a nesting error
 * and the row never renders — so a panel that is not a table uses this instead.
 */
export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="py-10 text-center text-erp-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
      {label}
    </div>
  )
}

export function ErrorStrip({ message, className }: { message?: string | null; className?: string }) {
  if (!message) return null
  return (
    <div className={cn(
      'px-4 py-2.5 text-erp-sm text-red-700 bg-red-50/60 border-b border-red-200 flex items-start gap-2',
      className,
    )}>
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span className="min-w-0">{message}</span>
    </div>
  )
}

export function EmptyState({
  title, hint, icon, action,
}: { title: string; hint?: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="px-4 py-12 text-center">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground mb-3">
        {icon ?? <Inbox className="h-4 w-4" />}
      </div>
      <div className="text-erp-sm font-medium">{title}</div>
      {hint && <div className="text-erp-xs text-muted-foreground mt-1 max-w-sm mx-auto">{hint}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

/** Rendered in place of a page the caller's authorities do not cover. */
export function Forbidden({ what }: { what: string }) {
  return (
    <div className="erp-card px-4 py-10 text-center">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-50 text-red-700 border border-red-200 mb-3">
        <ShieldAlert className="h-4 w-4" />
      </div>
      <div className="text-erp-sm font-medium">Access denied</div>
      <div className="text-erp-xs text-muted-foreground mt-1">
        You do not have permission to view {what}.
      </div>
    </div>
  )
}

/** Inline confirmation / result note, e.g. after revoking a session. */
export function Note({
  tone = 'info', children, onDismiss,
}: { tone?: 'info' | 'good' | 'warn' | 'bad'; children: ReactNode; onDismiss?: () => void }) {
  return (
    <div className={cn(
      'px-3 py-2 rounded-md border text-erp-sm flex items-start gap-2',
      tone === 'good' && 'bg-emerald-50 border-emerald-200 text-emerald-800',
      // `warn` is a caution, not a failure: the figure is real and readable, but it rests on
      // less than you would like. Red would say the screen is broken; the muted `info` grey
      // would let it pass unread, which for a thin-coverage projection is the worse error.
      tone === 'warn' && 'bg-amber-50 border-amber-200 text-amber-800',
      tone === 'bad' && 'bg-red-50 border-red-200 text-red-700',
      tone === 'info' && 'bg-muted/40 border-border text-muted-foreground',
    )}>
      <span className="min-w-0 flex-1">{children}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100 text-erp-xs">
          Dismiss
        </button>
      )}
    </div>
  )
}
